import { useEffect } from 'react';

type Breadcrumb = { name: string; url: string };
type Faq = { question: string; answer: string };
type SeoHeadProps = {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: 'website' | 'article' | 'product' | 'collection' | 'about';
  robots?: string;
  breadcrumbs?: Breadcrumb[];
  faq?: Faq[];
  pageSchema?: Record<string, unknown>[];
};

const setMeta = (key: 'name' | 'property', identifier: string, content: string) => {
  if (!content) return;
  let tag = document.querySelector<HTMLMetaElement>(`meta[${key}="${identifier}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(key, identifier);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setLink = (rel: string, href: string) => {
  if (!href) return;
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const setJsonLd = (id: string, data: unknown | null) => {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (!data) {
    existing?.remove();
    return;
  }
  const script: HTMLScriptElement = existing ?? document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  if (!existing) {
    document.head.appendChild(script);
  }
};

function SeoHead({
  title,
  description,
  url,
  image = 'https://frfisheries.com/og-fr-fisheries.webp',
  type = 'website',
  robots = 'index, follow',
  breadcrumbs,
  faq,
  pageSchema,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('name', 'googlebot', robots);

    setLink('canonical', url);

    setMeta('property', 'og:site_name', 'FR Fisheries');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type === 'article' ? 'article' : 'website');
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:locale', 'en_IN');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);

    const breadcrumbSchema = breadcrumbs?.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.url,
          })),
        }
      : null;
    setJsonLd('ld-breadcrumbs', breadcrumbSchema);

    const faqSchema = faq?.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;
    setJsonLd('ld-faq', faqSchema);

    const schemas: Record<string, unknown>[] = [];
    if (pageSchema?.length) {
      schemas.push(...pageSchema);
    }
    if (schemas.length === 1) {
      setJsonLd('ld-page', { '@context': 'https://schema.org', ...schemas[0] });
    } else if (schemas.length > 1) {
      setJsonLd('ld-page', { '@context': 'https://schema.org', '@graph': schemas });
    } else {
      setJsonLd('ld-page', null);
    }
  }, [title, description, url, image, type, robots, breadcrumbs, faq, pageSchema]);

  return null;
}

export default SeoHead;
